import type { ReportAggregation } from "@glorify-llc/accounting-core";

export interface GroupMember {
  id: string;
  personId: string;
  memberName: string;
  email: string;
  phone: string;
  role: "member" | "leader";
  joinDate: Date;
  status: string;
}

export type GroupRosterReportContext = {
  asOf: Date;
  members: GroupMember[];
};

// ---------------------------------------------------------------------------
// Raw-row → GroupMember helper (mirrors PR #1540 ar-aging, #1548 bank-recon)
// ---------------------------------------------------------------------------
//
// The `group_members` query in `useReportData.ts` returns raw rows
// (`person_id`, `created_at`, joined `people` data, etc.). The adapter
// expects `GroupMember` shape (camelCase, with `memberName` built from
// `people.first_name + last_name`). Feeding raw rows directly produces:
//   - personId → undefined → drilldown URL `/directory/undefined`
//   - memberName → undefined → blank labels in the table
//   - email/phone → undefined → blank cells
//   - joinDate → undefined → "Invalid Date"
//
// This helper centralizes the mapping so every wiring path is consistent.

export interface RawGroupMemberRow {
  id: string;
  person_id: string | null;
  role: string | null;
  // `group_members` has no status column (see useReportData.ts's
  // groupRosterQuery comment, #5974) — never populated by the live query
  // today. Kept optional, rather than removed, so a future status source
  // can be wired in without another interface change.
  status?: string | null;
  // The real join timestamp is `group_members.created_at` — there is no
  // separate `joined_at` column.
  created_at: string | null;
  group_id?: string | null;
  people?: {
    id?: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  groups?: { id?: string; name?: string } | null;
}

/**
 * Normalize a role string (case-insensitive). Anything that doesn't match
 * the typed enum collapses to `"member"` so a row with a corrupt or empty
 * role still appears in the roster.
 */
function normalizeRole(role: unknown): GroupMember["role"] {
  if (typeof role === "string" && role.trim().toLowerCase() === "leader") {
    return "leader";
  }
  return "member";
}

/**
 * Trim whitespace and lowercase status for stable downstream comparison.
 * The status enum at the data layer historically has "active"/"inactive"/
 * "pending" but mixed casing has bled in from imported data — same class
 * as PR #1547 (fund-health).
 */
function normalizeStatus(status: unknown): string {
  if (typeof status !== "string") return "unknown";
  const trimmed = status.trim().toLowerCase();
  return trimmed.length === 0 ? "unknown" : trimmed;
}

function buildMemberName(people: RawGroupMemberRow["people"]): string {
  if (!people) return "Unknown Member";
  const first = (people.first_name ?? "").trim();
  const last = (people.last_name ?? "").trim();
  const full = `${first} ${last}`.trim();
  return full.length > 0 ? full : "Unknown Member";
}

/**
 * Convert a raw `group_members` row into the typed shape the adapter expects.
 * Returns `null` for rows missing essential identity (id) so they don't
 * silently render as `Unknown Member` with a broken drilldown URL.
 */
export function bucketGroupMemberRow(raw: RawGroupMemberRow): GroupMember | null {
  if (!raw || !raw.id) return null;

  // Parse created_at (the real join timestamp) to a Date; tolerate
  // null/garbage by emitting an invalid Date that the adapter will treat as
  // "no join date" via Date validity checks downstream. Returning null here
  // would drop rows entirely, which is too aggressive — the rest of the row
  // (name, role, status) is still useful even without a join date.
  const joinDate = raw.created_at ? new Date(raw.created_at) : new Date(NaN);

  return {
    id: raw.id,
    personId: raw.people?.id || raw.person_id || "",
    memberName: buildMemberName(raw.people),
    email: raw.people?.email || "",
    phone: raw.people?.phone || "",
    role: normalizeRole(raw.role),
    joinDate,
    status: normalizeStatus(raw.status),
  };
}

// ---------------------------------------------------------------------------

export function buildGroupRosterReportAggregation(
  context: GroupRosterReportContext,
): ReportAggregation {
  const members = Array.isArray(context.members) ? context.members : [];

  // Single-pass tally: avoids two extra `filter().length` walks and keeps
  // counts perfectly aligned with the rows we actually emit.
  let leaders = 0;
  let activeMembers = 0;
  for (const m of members) {
    // Cheap defensive normalization for inputs that bypassed the
    // bucketing helper (e.g. test fixtures): treat role/status
    // case-insensitively.
    const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
    const status = typeof m.status === "string" ? m.status.toLowerCase() : "";
    if (role === "leader") leaders += 1;
    if (status === "active") activeMembers += 1;
  }

  const rows = members
    .map((member) => {
      // Only emit a drilldown URL when we actually have a personId.
      // Stale rows pointing to `/directory/` (empty path) or
      // `/directory/undefined` were silently broken in production.
      const drilldownUrl = member.personId
        ? `/directory/${encodeURIComponent(member.personId)}`
        : `/directory`;
      return {
        id: member.id,
        label: member.memberName || "Unknown Member",
        entityType: "person" as const,
        drilldownUrl,
        memberName: member.memberName || "Unknown Member",
        email: member.email || null,
        phone: member.phone || null,
        role: member.role,
        joinDate: member.joinDate,
        status: member.status,
      };
    })
    // Stable sort: leaders first, then alphabetic by member name. Locale-
    // independent (`<`/`>`) so a server in a different locale doesn't reorder
    // identical inputs across refetches.
    .sort((a, b) => {
      const aLeader = a.role === "leader" ? 1 : 0;
      const bLeader = b.role === "leader" ? 1 : 0;
      if (aLeader !== bLeader) return bLeader - aLeader;
      if (a.memberName < b.memberName) return -1;
      if (a.memberName > b.memberName) return 1;
      return 0;
    });

  return {
    asOf: context.asOf,
    summary: [
      { label: "Total Members", value: members.length },
      { label: "Leaders", value: leaders },
      { label: "Active Members", value: activeMembers },
    ],
    rows,
  };
}
